import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { Route, Switch } from 'react-router-dom';

import FullViewSpinner from '@provider-portal/common/components/FullViewSpinner/FullViewSpinner';
import { CommonApplicationServices } from '@provider-portal/common/services/CommonApplicationServices';
import { ProviderPortalNavigationService } from '@provider-portal/common/services/ProviderPortalNavigationService';
import { AppRoutes, AuthenticationRoutes, ProtectedRoutes } from 'AppRoutes';
import { ConnectedRouter, goBack, push } from 'connected-react-router';
import { History } from 'history';

import { NetworkRequestState, selectIsUserLoggedIn } from '@bolteu/redux-session';

import {
  requestAccountInfoAsyncAction,
  selectAccountRequestState,
  selectAccountStage,
  selectProviderAction,
  selectSelectedProvider,
} from 'rdx/account';
import { showNotificationAction } from 'rdx/notifications';
import { requestPromotionsPermissionAsyncAction } from 'rdx/promotions/actions';
import { selectAccountId } from 'rdx/session/selectors';
import { requestTargetingParametersAsyncAction } from 'rdx/targetingParameters/actions';
import {
  selectTargetingParameters,
  selectTargetingParametersIsRequestFinished,
} from 'rdx/targetingParameters/selectors';
import { RemoteIntlProvider, setGlobalIntl } from 'utils/translations';

import Notifications from 'components/Notifications/Notifications';
import { CommonAppServicesContextProvider } from 'root/CommonAppServicesContext';
import { ProviderPortalModuleManifest } from 'root/ProviderPortalApp';
import { LoginWithToken } from 'screens/Login/LoginWithToken';
import { Logout } from 'screens/Login/Logout';
import { SupportCaseRedirect } from 'screens/SupportCaseRedirect/SupportCaseRedirect';

import { AccountStage } from '../../common/all/services/types/account';

interface AppContainerProps {
  history: History;
  commonAppServices: CommonApplicationServices;
  moduleManifests: ProviderPortalModuleManifest[];
  navigationService: ProviderPortalNavigationService;
}

const DummyComponentToSetGlobalIntl: React.FC<Record<string, any>> = () => {
  const intl = useIntl();
  const dispatch = useDispatch();

  // dirty hack that allows us to use intl in epics.
  useEffect(() => {
    setGlobalIntl(intl);
  }, [intl, dispatch]);

  return null;
};

export const AppContainer: React.FC<AppContainerProps> = ({
  history,
  moduleManifests,
  commonAppServices,
  navigationService,
  children: moduleContent,
}) => {
  const { accountService, eventAliasingService } = commonAppServices;
  const accountId = useSelector(selectAccountId);
  const accountStage = useSelector(selectAccountStage);

  const changeProviderSubject = useMemo(
    () => accountService.getChangeProviderSubject$(),
    [accountService],
  );
  const showNotificationSubject = useMemo(
    () => accountService.getShowNotificationSubject$(),
    [accountService],
  );
  const store = useStore();
  const isUserLoggedIn = useSelector(selectIsUserLoggedIn);
  const accountRequestState = useSelector(selectAccountRequestState);

  const [translationsRequestState, setTranslationsRequestState] = useState(
    NetworkRequestState.unset,
  );
  const selectedProvider = useSelector(selectSelectedProvider);
  const dispatch = useDispatch();
  const isTargetingParametersRequestFinished = useSelector(
    selectTargetingParametersIsRequestFinished,
  );
  const targetingParameters = useSelector(selectTargetingParameters);

  useEffect(() => {
    if (accountId) {
      // Once logged in, we set distinctId for our further tracking
      const mixpanelUserId = `provider-portal-account-${accountId}`;
      commonAppServices.mixpanelService.identify(mixpanelUserId);

      if (eventAliasingService.hasLeadIdStored()) {
        eventAliasingService.linkLeadAndProvider(mixpanelUserId);
      }
    } else {
      // We need to reset mixPanel once user is logged out
      commonAppServices.mixpanelService.reset();
    }
  }, [accountId, commonAppServices, eventAliasingService]);

  useEffect(() => {
    if (isUserLoggedIn) {
      dispatch(requestAccountInfoAsyncAction.request());
      dispatch(requestTargetingParametersAsyncAction.request());
    }
  }, [dispatch, isUserLoggedIn]);

  useEffect(() => {
    if (selectedProvider && accountStage === AccountStage.ACTIVATED) {
      dispatch(requestPromotionsPermissionAsyncAction.request());
    }
  }, [dispatch, selectedProvider, accountStage]);

  const switchProvider = useCallback(
    (linkProviderId: number) => {
      dispatch(selectProviderAction(linkProviderId));
    },
    [dispatch],
  );

  useEffect(() => {
    const providerSubject = changeProviderSubject.subscribe({
      next: (linkProviderId) => switchProvider(linkProviderId),
    });
    const notificationSubject = showNotificationSubject.subscribe({
      next: (intlId: string) => dispatch(showNotificationAction({ intlId })),
    });

    return () => {
      notificationSubject.unsubscribe();
      providerSubject.unsubscribe();
    };
  }, [changeProviderSubject, switchProvider, dispatch, showNotificationSubject]);

  const handleTranslationsSuccess = useCallback(() => {
    setTranslationsRequestState(NetworkRequestState.success);
  }, []);

  const handleTranslationsFailure = useCallback(() => {
    setTranslationsRequestState(NetworkRequestState.failure);
  }, []);

  const publicRoutes = useMemo(() => {
    return moduleManifests.map((manifest) => {
      if (!manifest.isPublic) {
        return null;
      }
      return (
        <Route path={`/${manifest.moduleName}(/*)`} key={manifest.moduleName}>
          {moduleContent}
        </Route>
      );
    });
  }, [moduleContent, moduleManifests]);

  useEffect(() => {
    const subscripton = navigationService.onNavigationCall((navCall) => {
      if (navCall.type === 'navigateTo') {
        store.dispatch(push(navCall.url));
      } else if (navCall.type === 'goBack') {
        store.dispatch(goBack());
      }
    });
    return () => subscripton.unsubscribe();
  }, [navigationService, store]);

  const isTranslationsRequestFinished =
    translationsRequestState !== NetworkRequestState.unset &&
    translationsRequestState !== NetworkRequestState.waiting;

  const isUserInitializationFinished =
    accountRequestState === NetworkRequestState.success || !isUserLoggedIn;

  return (
    <RemoteIntlProvider
      onTranslationsFetchSuccess={handleTranslationsSuccess}
      onTranslationsFetchError={handleTranslationsFailure}
    >
      <CommonAppServicesContextProvider value={commonAppServices}>
        <DummyComponentToSetGlobalIntl />
        <Notifications />
        {isTranslationsRequestFinished &&
        isUserInitializationFinished &&
        (isUserLoggedIn ? isTargetingParametersRequestFinished : true) ? (
          <ConnectedRouter history={history}>
            <Switch>
              {publicRoutes}
              <Route path={AppRoutes.AUTH_WITH_TOKEN} key={AppRoutes.AUTH_WITH_TOKEN}>
                <LoginWithToken />
              </Route>
              <Route path={AppRoutes.LOGOUT} key={AppRoutes.LOGOUT} exact>
                <Logout />
              </Route>
              <Route path={AppRoutes.SUPPORT_REDIRECT} key={AppRoutes.SUPPORT_REDIRECT} exact>
                <SupportCaseRedirect />
              </Route>

              {isUserLoggedIn ? (
                <ProtectedRoutes
                  moduleManifests={moduleManifests}
                  targetingParameters={targetingParameters}
                >
                  {' '}
                  {moduleContent}{' '}
                </ProtectedRoutes>
              ) : (
                <AuthenticationRoutes />
              )}
            </Switch>
          </ConnectedRouter>
        ) : (
          <FullViewSpinner />
        )}
      </CommonAppServicesContextProvider>
    </RemoteIntlProvider>
  );
};
